import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { PlanDto } from "@/types/api";

/**
 * Public plans catalogue. Anonymous — both the marketing /plans page AND
 * the in-dashboard "Change plan" surface call this same hook.
 *
 * The optional `planType` filter maps to the backend's `?type=` query so
 * we can fetch a single section (e.g. "email") without scanning all 9.
 */
export function usePlans(planType?: "email" | "hosting" | "portfolio") {
  return useQuery({
    queryKey: ["plans", planType ?? "all"],
    queryFn: ({ signal }) => {
      const path = planType ? `/api/plans?type=${planType}` : "/api/plans";
      return api.get<PlanDto[]>(path, signal);
    },
    // Catalogue is mostly static; let it sit fresh for an hour. Admin
    // updates via PATCH /plans/:slug will eventually trigger an
    // invalidate when we add admin UI.
    staleTime: 60 * 60_000
  });
}

/** Single plan by slug — used by the purchase flow (/buy/:slug). */
export function usePlan(slug: string | undefined) {
  return useQuery({
    queryKey: ["plans", "one", slug],
    enabled: Boolean(slug),
    queryFn: ({ signal }) => api.get<PlanDto>(`/api/plans/${slug}`, signal),
    staleTime: 60 * 60_000
  });
}
